/**
 * Hook for bulk updating response actions (Accept/Reject multiple at once)
 *
 * Provides optimistic updates for snappy UX while persisting to API in parallel.
 */

import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryKeys';
import { updateResponseAction } from '@/api/endpoints';
import { useSessionStore } from '@/stores/useSessionStore';
import { useReviewStore } from '@/stores/useReviewStore';
import type { UserActionType, DdqSessionQuestion } from '@/types/api.types';
import { isBulkAcceptEligible } from '../utils/bulkAcceptEligibility';

export interface UseBulkUpdateActionReturn {
  bulkAccept: (questionIds: string[]) => Promise<number>;
  bulkReject: (questionIds: string[]) => Promise<number>;
  bulkAcceptAboveThreshold: (
    threshold: number,
    questions: DdqSessionQuestion[]
  ) => Promise<number>;
  isPending: boolean;
  successCount: number;
  errorCount: number;
}

interface BulkUpdateParams {
  sessionId: string;
  responseId: string;
  action: UserActionType;
  modifiedAnswer?: string;
}

export function useBulkUpdateAction(sessionId: string): UseBulkUpdateActionReturn {
  const queryClient = useQueryClient();
  const updateQuestion = useSessionStore((s) => s.updateQuestion);
  const questions = useSessionStore((s) => s.questions);
  const clearSelection = useReviewStore((s) => s.clearSelection);

  const [isPending, setIsPending] = useState(false);
  const [successCount, setSuccessCount] = useState(0);
  const [errorCount, setErrorCount] = useState(0);

  // Single mutation for API call
  const mutation = useMutation({
    mutationFn: (params: BulkUpdateParams) =>
      updateResponseAction(params.sessionId, params.responseId, {
        action: params.action,
        modified_answer: params.modifiedAnswer,
      }),
  });

  // Bulk accept selected questions
  const bulkAccept = useCallback(
    async (questionIds: string[]): Promise<number> => {
      if (!sessionId || questionIds.length === 0) return 0;

      setIsPending(true);
      setSuccessCount(0);
      setErrorCount(0);

      const now = new Date().toISOString();
      let success = 0;
      let errors = 0;

      // Filter to only pending questions
      const pendingIds = questionIds.filter((id) => {
        const q = questions.find((question) => question.question_id === id);
        return q && (!q.userAction || q.userAction === 'Pending');
      });

      // Optimistic updates for all
      for (const questionId of pendingIds) {
        updateQuestion(questionId, {
          userAction: 'Accepted',
          modifiedDate: now,
        });
      }

      // Persist to API in parallel (batched)
      const results = await Promise.allSettled(
        pendingIds.map((questionId) =>
          mutation.mutateAsync({
            sessionId,
            responseId: questionId,
            action: 'Accept',
          })
        )
      );

      // Count successes/errors
      for (let i = 0; i < results.length; i++) {
        if (results[i].status === 'fulfilled') {
          success++;
        } else {
          errors++;
          // Rollback optimistic update on failure
          const questionId = pendingIds[i];
          const original = questions.find((q) => q.question_id === questionId);
          if (original) {
            updateQuestion(questionId, {
              userAction: original.userAction ?? 'Pending',
              modifiedDate: original.modifiedDate,
            });
          }
        }
      }

      setSuccessCount(success);
      setErrorCount(errors);
      setIsPending(false);

      // Clear selection after bulk action
      clearSelection();

      // Invalidate queries to refresh
      queryClient.invalidateQueries({ queryKey: queryKeys.sessionDetails(sessionId) });

      return success;
    },
    [sessionId, questions, updateQuestion, mutation, clearSelection, queryClient]
  );

  // Bulk reject selected questions
  const bulkReject = useCallback(
    async (questionIds: string[]): Promise<number> => {
      if (!sessionId || questionIds.length === 0) return 0;

      setIsPending(true);
      setSuccessCount(0);
      setErrorCount(0);

      const now = new Date().toISOString();
      let success = 0;
      let errors = 0;

      // Filter to only pending questions
      const pendingIds = questionIds.filter((id) => {
        const q = questions.find((question) => question.question_id === id);
        return q && (!q.userAction || q.userAction === 'Pending');
      });

      // Optimistic updates for all
      for (const questionId of pendingIds) {
        updateQuestion(questionId, {
          userAction: 'Rejected',
          modifiedDate: now,
        });
      }

      // Persist to API in parallel (batched)
      const results = await Promise.allSettled(
        pendingIds.map((questionId) =>
          mutation.mutateAsync({
            sessionId,
            responseId: questionId,
            action: 'Reject',
          })
        )
      );

      // Count successes/errors
      for (let i = 0; i < results.length; i++) {
        if (results[i].status === 'fulfilled') {
          success++;
        } else {
          errors++;
          // Rollback optimistic update on failure
          const questionId = pendingIds[i];
          const original = questions.find((q) => q.question_id === questionId);
          if (original) {
            updateQuestion(questionId, {
              userAction: original.userAction ?? 'Pending',
              modifiedDate: original.modifiedDate,
            });
          }
        }
      }

      setSuccessCount(success);
      setErrorCount(errors);
      setIsPending(false);

      // Clear selection after bulk action
      clearSelection();

      // Invalidate queries to refresh
      queryClient.invalidateQueries({ queryKey: queryKeys.sessionDetails(sessionId) });

      return success;
    },
    [sessionId, questions, updateQuestion, mutation, clearSelection, queryClient]
  );

  // Bulk accept questions above a confidence threshold.
  // Floor rule 2 (BUG-008): review-flagged answers are excluded no matter
  // how high their confidence is - see isBulkAcceptEligible.
  const bulkAcceptAboveThreshold = useCallback(
    async (
      threshold: number,
      questionsToCheck: DdqSessionQuestion[]
    ): Promise<number> => {
      if (!sessionId) return 0;

      const eligibleIds = questionsToCheck
        .filter((q) => isBulkAcceptEligible(q, threshold))
        .map((q) => q.question_id);

      if (eligibleIds.length === 0) return 0;

      return bulkAccept(eligibleIds);
    },
    [sessionId, bulkAccept]
  );

  return {
    bulkAccept,
    bulkReject,
    bulkAcceptAboveThreshold,
    isPending,
    successCount,
    errorCount,
  };
}
